import type { ComponentType } from "react";
import { ViewsNav } from "@omnabase/ui";
import { Route, Routes, useParams } from "react-router";
import { useNav, useWs } from "../../lib/nav";
import { SECTIONS, useSectionAllowed, useSettingsGroups } from "../../lib/settingsNav";
import { Shell } from "../../shell/Shell";
import { GeneralSettings } from "./GeneralSettings";
import { ProfileSettings } from "./ProfileSettings";
import { NotificationSettings } from "./NotificationSettings";
import { TeamScreen } from "./TeamScreen";
import { ProjectsScreen } from "./ProjectsScreen";
import { ConnectMailScreen } from "./ConnectMailScreen";
import { TagsSettings } from "./TagsSettings";
import { BlockedSenders } from "./BlockedSenders";
import { SavedRepliesSettings } from "./SavedRepliesSettings";
import { AiSettings } from "./AiSettings";
import { YourOwnAiSettings } from "./YourOwnAiSettings";
import { IntegrationsSettings } from "./IntegrationsSettings";
import { BillingSettings } from "./BillingSettings";
import { LegalSettings } from "./LegalSettings";
import { FormsSettings } from "./FormsSettings";
import { FormBuilderScreen } from "./FormBuilderScreen";
import { NoSuchPage } from "./NoSuchPage";
import { useScrollReset } from "../../lib/scrollReset";

/** Settings: the views column lists the sections; each section is a page. Team and Projects bring their own full-width pages, and so do
 *  Integrations and Billing inside `SettingsPage` (2026-10-13). */
export function SettingsRoutes() {
  return (
    <Routes>
      <Route path="team/roles/:roleId" element={<TeamScreen view="roles" />} />
      <Route path="team/roles" element={<TeamScreen view="roles" />} />
      <Route path="team/hours" element={<TeamScreen view="hours" />} />{/* the working hours round (2026-11-01): before team/:memberId, explicitly */}
      <Route path="team/:memberId" element={<TeamScreen />} />
      <Route path="team" element={<TeamScreen />} />
      <Route path="projects/:projectId/connect-mail" element={<ConnectMailScreen />} />
      <Route path="projects/:projectId" element={<ProjectsScreen />} />
      <Route path="projects" element={<ProjectsScreen />} />
      <Route path="forms/:formId" element={<FormBuilderScreen />} />{/* the form builder, round one (2026-10-25) */}
      <Route path=":section" element={<SettingsPage />} />
      <Route index element={<SettingsPage section="general" />} />
    </Routes>
  );
}

/** Pages that bring their own full-width head with the kit's tab strip, as Team does (the founder, 2026-10-13): they sit straight in the
 *  shell's body, so the hairline under the tabs runs the whole width of the content area, not inside the page's padding. AI joined them with its
 *  tabs (2026-10-18; four since the polish round, Your own AI being its own page). */
const OWN_HEAD: Partial<Record<string, ComponentType>> = { integrations: IntegrationsSettings, billing: BillingSettings, ai: AiSettings, tags: TagsSettings, notifications: NotificationSettings }; // notifications: the device notifications round's three tabs (2026-11-01); tags: the customer tags round's two tabs (2026-10-24)
/** the pages inside the padded page, wide (`ob-page`) or centred */
const WIDE = ["general", "profile", "your-ai", "saved-replies", "blocked", "legal", "forms"];
/** the pages drawn inside the padded page, by section id */
const PAGES: Partial<Record<string, ComponentType>> = { general: GeneralSettings, profile: ProfileSettings, "your-ai": YourOwnAiSettings, "saved-replies": SavedRepliesSettings, blocked: BlockedSenders, legal: LegalSettings, forms: FormsSettings }; // legal (2026-10-20): Legal & data; forms (2026-10-25): the form builder's list; your-ai (2026-10-31): Your own AI, out of the AI page's tabs

function SettingsPage({ section: given }: { section?: string }) {
  const nav = useNav();
  const { ws } = useWs();
  const params = useParams();
  const asked = given ?? params.section ?? "general";
  const section = useSectionAllowed()(asked) ? asked : "none"; // a section not theirs by its address is the plain not-found
  const groups = useSettingsGroups(section);
  const label = SECTIONS.find((s) => s.id === section)?.label ?? "Not found";
  const OwnHead = OWN_HEAD[section]; const Page = PAGES[section] ?? NoSuchPage;
  const top = useScrollReset(section); // a section starts at the top (polish round one)
  return (
    <Shell views={<ViewsNav groups={groups} onNavigate={nav} />} crumbs={[{ label: "Settings", href: ws("/settings/general") }, { label, views: true }]} bodyClassName="ob-body--dashboard">
      {OwnHead ? <OwnHead /> : (
        <main className={`ob-page${WIDE.includes(section) ? "" : " ob-page--centred"}`} ref={top}>
          <Page />
        </main>
      )}
    </Shell>
  );
}
